import OrganizationUserList, {
  DISPLAY_FILTERS,
  SORT,
  determineSortIcon,
} from "byzantine/src/OrganizationUserList";
import Filters from "byzantine/src/filters";
import {
  Button,
  Chip,
  IconButton,
  Popover,
  LoadingShim,
  TextInput,
  Table,
  Tooltip,
} from "@narmi/design_system";

import PropTypes from "prop-types";
import React from "react";
import { useLocalization } from "@fluent/react";
import { Check } from "react-feather";

import BaseBanner from "../BaseBanner";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import FilterCheckbox from "../functional/FilterCheckbox";

// We have some hard coded English copy in byzantine that we must
// map to fluent ids for the OLB product FTL
const BYZANTINE_COPY_TO_FLUENT_ID = {
  Active: "org-user-status-active",
  Pending: "org-user-status-invited",
  Inactive: "org-user-status-deactivated",
  "Account Holder": "org-user-role-account-holder",
  Admin: "org-user-role-admin",
  Viewer: "org-user-role-viewer",
  Collaborator: "org-user-role-collaborator",
  Deactivated: "org-user-status-deactivated",
};

const STATUS_CHIP_KINDS = {
  Active: "success",
  Pending: "warn",
  Inactive: "info",
};

const STATUS_CHIP_ICONS = {
  Active: "check",
  Pending: "hourglass",
  Inactive: "x",
};

const getFilterCount = (filters, displayFilters) => {
  const count = filters.filter((f) =>
    displayFilters.some((df) => df.filter === f)
  ).length;
  return count || undefined;
};

const FilterPopoverContent = ({
  displayFilters,
  pendingFilters,
  setPendingFilters,
  filterType,
  onClose,
  filters,
  toggleFilter,
  setFilteringByRole,
  setFilteringByStatus,
  l10n,
}) => (
    <div style={{ padding: "8px" }}>
      <div
        style={{
          width: "213px",
          display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        {displayFilters.map(({ text, filter }) => (
          <div
            key={text}
            role="button"
            tabIndex="0"
            onClick={() => {
              setPendingFilters((prev) =>
                prev.includes(filter)
                  ? prev.filter((f) => f !== filter)
                  : [...prev, filter]
              );
            }}
            onKeyUp={({ key }) => {
              if (key === "Enter") {
                setPendingFilters((prev) =>
                  prev.includes(filter)
                    ? prev.filter((f) => f !== filter)
                    : [...prev, filter]
                );
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <FilterCheckbox
              label={l10n.getString(BYZANTINE_COPY_TO_FLUENT_ID[text])}
              filters={pendingFilters}
              toggleFilter={() => {
                setPendingFilters((prev) =>
                  prev.includes(filter)
                    ? prev.filter((f) => f !== filter)
                    : [...prev, filter]
                );
              }}
              filter={filter}
            />
          </div>
        ))}
      </div>
      <hr
        style={{
          margin: "8px 0",
          border: "none",
          borderTop: "1px solid rgb(var(--border-color))",
        }}
      />
      <div
        style={{
          padding: "8px",
          display: "flex",
          gap: "8px",
          justifyContent: "space-between",
        }}
      >
        <Button
          kind="negative"
          label={l10n.getString("org-user-filter-remove")}
          onClick={() => {
            displayFilters.forEach(({ filter }) => {
              if (filters.includes(filter)) {
                toggleFilter(filter);
              }
            });
            if (filterType === "ROLE") setFilteringByRole(false);
            if (filterType === "STATUS") setFilteringByStatus(false);
            onClose?.();
          }}
          size="xs"
        />
        <Button
          kind="primary"
          label={l10n.getString("org-user-filter-apply")}
          onClick={() => {
            const nonTypeFilters = filters.filter(
              (f) => !displayFilters.some((df) => df.filter === f)
            );
            const newFilters = [...nonTypeFilters, ...pendingFilters];
            newFilters.forEach((filter) => {
              if (!filters.includes(filter)) {
                toggleFilter(filter);
              }
            });
            filters.forEach((filter) => {
              if (
                !newFilters.includes(filter) &&
                displayFilters.some((df) => df.filter === filter)
              ) {
                toggleFilter(filter);
              }
            });
            onClose?.();
          }}
          size="xs"
        />
      </div>
    </div>
  );

FilterPopoverContent.propTypes = {
  displayFilters: PropTypes.array.isRequired,
  pendingFilters: PropTypes.array.isRequired,
  setPendingFilters: PropTypes.func.isRequired,
  filterType: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  filters: PropTypes.array.isRequired,
  toggleFilter: PropTypes.func.isRequired,
  setFilteringByRole: PropTypes.func.isRequired,
  setFilteringByStatus: PropTypes.func.isRequired,
  l10n: PropTypes.object.isRequired,
};

export function Header({ search, setSearch, filters, toggleFilter }) {
  const { l10n } = useLocalization();
  const [filteringByRole, setFilteringByRole] = React.useState(false);
  const [filteringByStatus, setFilteringByStatus] = React.useState(false);
  const [isRolePopoverOpen, setIsRolePopoverOpen] = React.useState(false);
  const [isStatusPopoverOpen, setIsStatusPopoverOpen] = React.useState(false);
  const [pendingRoleFilters, setPendingRoleFilters] = React.useState([]);
  const [pendingStatusFilters, setPendingStatusFilters] = React.useState([]);

  React.useEffect(() => {
    if (isRolePopoverOpen) {
      setPendingRoleFilters(
        filters.filter((f) =>
          DISPLAY_FILTERS.ROLE.some((rf) => rf.filter === f)
        )
      );
    }
  }, [isRolePopoverOpen, filters]);

  React.useEffect(() => {
    if (isStatusPopoverOpen) {
      setPendingStatusFilters(
        filters.filter((f) =>
          DISPLAY_FILTERS.STATUS.some((sf) => sf.filter === f)
        )
      );
    }
  }, [isStatusPopoverOpen, filters]);

  const TeamBanner = (
    <BaseBanner
      bannerName={l10n.getString("banner-manage-team")}
      features={{ business_banking: true }}
      orFeatureFlagChecks={["business_banking"]}
    />
  );

  const SearchInput = (
    <TextInput
      startIcon={"search"}
      onChange={(event) => setSearch(event.target.value)}
      placeholder={l10n.getString("manage-users-search-by-name-or-email")}
      value={search}
    />
  );

  const filterChip = ({ displayFilters, pendingFilters, setPendingFilters, filterType, isOpen, setIsOpen, label }) => (
      <div className="org-users-left-aligned-popover" style={{ display: "inline-block" }}>
        <Popover 
          content={
            <FilterPopoverContent 
              displayFilters={displayFilters} 
              pendingFilters={pendingFilters} 
              setPendingFilters={setPendingFilters} 
              filterType={filterType} 
              onClose={() => setIsOpen(false)}
              filters={filters}
              toggleFilter={toggleFilter}
              setFilteringByRole={setFilteringByRole}
              setFilteringByStatus={setFilteringByStatus}
              l10n={l10n}
            />
          } 
          wrapperDisplay="inline" 
          onUserDismiss={() => setIsOpen(false)}
        >
          <Chip
            kind="secondary"
            endIcon={isOpen ? "chevron-up" : "chevron-down"}
            label={label}
            count={getFilterCount(filters, displayFilters)}
            onClick={() => setIsOpen(!isOpen)}
          />
        </Popover>
      </div>
    );

  const ChipFilters = () => {
    const PopoverContent = () => (
      <div style={{ padding: "8px" }}>
        <div
          style={{
            width: "138px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <div
            role="button"
            tabIndex="0"
            onClick={() => setFilteringByRole(!filteringByRole)}
            onKeyUp={({ key }) => {
              if (key === "Enter") {
                setFilteringByRole(!filteringByRole);
              }
            }}
            style={{
              padding: "12px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Role</span>
            {filteringByRole && <Check size={16} />}
          </div>
          <div
            role="button"
            tabIndex="0"
            onClick={() => setFilteringByStatus(!filteringByStatus)}
            onKeyUp={({ key }) => {
              if (key === "Enter") {
                setFilteringByStatus(!filteringByStatus);
              }
            }}
            style={{
              padding: "12px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Status</span>
            {filteringByStatus && <Check size={16} />}
          </div>
        </div>
      </div>
    );

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "16px",
          width: "fit-content",
        }}
      >
        {filteringByRole &&
          filterChip({
            displayFilters: DISPLAY_FILTERS.ROLE,
            pendingFilters: pendingRoleFilters,
            setPendingFilters: setPendingRoleFilters,
            filterType: "ROLE",
            isOpen: isRolePopoverOpen,
            setIsOpen: setIsRolePopoverOpen,
            label: l10n.getString("org-user-filter-role"),
          })}
        {filteringByStatus &&
          filterChip({
            displayFilters: DISPLAY_FILTERS.STATUS,
            pendingFilters: pendingStatusFilters,
            setPendingFilters: setPendingStatusFilters,
            filterType: "STATUS",
            isOpen: isStatusPopoverOpen,
            setIsOpen: setIsStatusPopoverOpen,
            label: l10n.getString("org-user-filter-status"),
          })}
        <div className="org-users-left-aligned-popover" style={{ display: "inline-block" }}>
          <Popover
            content={<PopoverContent />}
            wrapperDisplay="inline"
          >
            <Chip
              kind="secondary"
              startIcon={"filter-2"}
              isLabelVisible={false}
            />
          </Popover>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="organization-users-team-banner">{TeamBanner}</div>
      <div className="mobile-only" style={{ marginBottom: "20px" }}>
        <Button
          kind="primary"
          startIcon="plus"
          label={l10n.getString("button-add-new-user")}
          size="xs"
          onClick={() => window.location.assign("/manage_users/new_user")}
        />
      </div>
      <div className="searchAndFilterContainer">
        <div
          className="search inputBox nds-input-style-override"
          data-testid="input-box"
        >
          {SearchInput}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "16px",
        }}
      >
        {ChipFilters()}
        <div className="mobile-hidden">
          <Button
            kind="primary"
            startIcon="plus"
            label={l10n.getString("button-add-new-user")}
            size="xs"
            onClick={() => window.location.assign("/manage_users/new_user")}
          />
        </div>
      </div>
    </div>
  );
}

Header.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func,
  filters: PropTypes.array,
  toggleFilter: PropTypes.func,
};

export const MemberNameAndErrorPopup = (props) => {
  const memberName = `${props.firstName} ${props.lastName}`;

  return (
    <Tooltip
      text={props.content || ""}
      side="top"
      isOpen={props.isVisible && !!props.content}
      testId="memberNameAndErrorPopup"
    >
      <div>
        <div className="fontSize--s">{memberName}</div>
        <p className="fontSize--s fontColor--secondary">{props.email}</p>
      </div>
    </Tooltip>
  );
};

MemberNameAndErrorPopup.propTypes = {
  content: PropTypes.string,
  isVisible: PropTypes.bool,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
};

const OrganizationUserRow = ({ member }) => {
  const [isErrorShown, setIsErrorShown] = React.useState(false);
  const { currentUser } = useCurrentUser();
  const { l10n } = useLocalization();

  // attach mouseleave to the rendered row DOM node so the error tooltip
  // closes when the mouse leaves the row, regardless of which cell was clicked
  const rowCallbackRef = React.useCallback((node) => {
    if (!node) return;
    const row = node.closest(".nds-table-row");
    if (row) {
      row.addEventListener("mouseleave", () => setIsErrorShown(false));
    }
  }, []);

  if (!currentUser) {
    return <LoadingShim loading={true} />;
  }
  const reason = member.reasonIfNotEditable(
    currentUser.org_role,
    currentUser.org_uuid
  );

  const goToEditUser = () => {
    if (reason) {
      // error reason only pops up on click
      setIsErrorShown(true);
      return;
    }
    window.location = `/manage_users/edit_user/${member.uuid}`;
  };

  return (
    <Table.Row onRowClick={() => goToEditUser()}>
      <Table.Cell>
        <div ref={rowCallbackRef}>
          <MemberNameAndErrorPopup
            isVisible={isErrorShown}
            content={reason}
            firstName={member.user.first_name}
            lastName={member.user.last_name}
            email={member.user.email}
          />
        </div>
      </Table.Cell>
      <Table.Cell>{member.role.getAccessLevelUnlessIsOwner()}</Table.Cell>
      <Table.Cell>
        <Chip
          startIcon={STATUS_CHIP_ICONS[member.status]}
          kind={STATUS_CHIP_KINDS[member.status]}
          label={l10n.getString(BYZANTINE_COPY_TO_FLUENT_ID[member.status])}
        />
      </Table.Cell>
      <Table.Cell>
        {member.user.last_login
          ? Filters.shortMonthDayYear(member.user.last_login)
          : ""}
      </Table.Cell>
    </Table.Row>
  );
};
OrganizationUserRow.propTypes = {
  member: PropTypes.object,
};

const OrganizationUserMobileRow = ({ member }) => {
  /* the responsive version of `OrganizationUserRow`
     note that it currently does not display the reason a row is uneditable
   */
  const { currentUser } = useCurrentUser();
  const { l10n } = useLocalization();

  if (!currentUser) {
    return <LoadingShim loading={true} />;
  }

  const reason = member.reasonIfNotEditable(
    currentUser?.org_role,
    currentUser?.org_uuid
  );

  const goToEditUser = () => {
    if (!reason) {
      window.location = `/manage_users/edit_user/${member.uuid}`;
    }
  };

  return (
    <div
      className="mobileListContents"
      role="button"
      tabIndex="0"
      onKeyUp={({ key }) => {
        if (key === "Enter") {
          goToEditUser();
        }
      }}
      onClick={() => {
        goToEditUser();
      }}
    >
      <div className="titleLine">
        <div className="statusChipWrap">
          <Chip
            startIcon={STATUS_CHIP_ICONS[member.status]}
            kind={STATUS_CHIP_KINDS[member.status]}
            label={l10n.getString(BYZANTINE_COPY_TO_FLUENT_ID[member.status])}
            isLabelVisible={false}
          />
        </div>
        <div className="titleLineText">
          <div className="memberText">
            <div className="name">
              {member.user.first_name} {member.user.last_name}
            </div>
            <div>{member.user.email}</div>
          </div>
          <div className="fontWeight--bold">{member.role.getAccessLevel()}</div>
        </div>
      </div>
    </div>
  );
};

OrganizationUserMobileRow.propTypes = {
  member: PropTypes.object,
};

export const Contents = (props) => {
  const { l10n } = useLocalization();

  return (
    <div>
      <div className="mobile-hidden">
        <Table
          kind="editable"
          colVisibility={["*", "*", "*", "*"]}
          colLayout={{
            s: "1fr 1fr 1fr 1fr",
            m: "25% 25% 25% 25%",
            l: "25% 35% 25% 15%",
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell suffixIcon={"arrow-up-down"}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {l10n.getString("org-user-column-name")}
                  <div style={{ marginLeft: "8px" }}>
                    <IconButton
                      name={props.determineSortIcon("name", props.sortOrder)}
                      onClick={() =>
                        props.setSortOrder(() =>
                          props.sortOrder === SORT.NAME_ASC
                            ? SORT.NAME_DESC
                            : SORT.NAME_ASC
                        )
                      }
                    />
                  </div>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("org-user-column-role")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("org-user-column-status")}
              </Table.HeaderCell>
              <Table.HeaderCell suffixIcon={"arrow-up-down"}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {l10n.getString("org-user-column-last-login")}
                  <div style={{ marginLeft: "8px" }}>
                    <IconButton
                      name={props.determineSortIcon(
                        "lastlogin",
                        props.sortOrder
                      )}
                      onClick={() =>
                        props.setSortOrder(() =>
                          props.sortOrder === SORT.LASTLOGIN_ASC
                            ? SORT.LASTLOGIN_DESC
                            : SORT.LASTLOGIN_ASC
                        )
                      }
                    />
                  </div>
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {props.displayOrganizationUsers.map((member) => (
              <OrganizationUserRow key={member.uuid} member={member} />
            ))}
          </Table.Body>
        </Table>
      </div>
      <div className="mobile-only">
        {props.displayOrganizationUsers.map((member) => (
          <OrganizationUserMobileRow key={member.uuid} member={member} />
        ))}
      </div>
    </div>
  );
};

Contents.propTypes = {
  displayOrganizationUsers: PropTypes.array,
  sortOrder: PropTypes.func,
  setSortOrder: PropTypes.func,
  determineSortIcon: PropTypes.func,
};

export const Container = (props) => {
  const { l10n } = useLocalization();

  const NoResults = () => {
    const noResultsStyle = {
      backgroundColor: "rgb(var(--background-grey-color))",
      padding: "32px 0px 0px 4px",
    };
    const largerTextStyle = {
      color: "black",
      fontSize: "var(--font-size-l)",
      lineHeight: "100%",
      fontWeight: "bold",
      paddingBottom: "8px",
      marginBottom: "0px",
    };
    const smallerTextStyle = {
      fontSize: "var(--font-size-m)",
      lineHeight: "100%",
    };

    return (
      <div style={noResultsStyle}>
        <p style={largerTextStyle}>
          {l10n.getString("manage-users-no-users-found")}
        </p>
        <p style={smallerTextStyle}>
          {l10n.getString("manage-users-adjust-your-filters")}
        </p>
      </div>
    );
  };

  const contents =
    props.displayOrganizationUsers.length === 0 ? (
      <NoResults />
    ) : (
      <Contents
        organizationUsers={props.organizationUsers}
        displayOrganizationUsers={props.displayOrganizationUsers}
        sortOrder={props.sortOrder}
        setSortOrder={props.setSortOrder}
        determineSortIcon={props.determineSortIcon}
      />
    );
  return (
    <div className="OrganizationUserList">
      <Header
        search={props.search}
        setSearch={props.setSearch}
        filters={props.filters}
        toggleFilter={props.toggleFilter}
        sortOrder={props.sortOrder}
        setSortOrder={props.setSortOrder}
        determineSortIcon={props.determineSortIcon}
      />
      <div className="padding--top--xl">{contents}</div>
    </div>
  );
};

Container.propTypes = {
  organizationUsers: PropTypes.array,
  displayOrganizationUsers: PropTypes.array,
  search: PropTypes.string,
  setSearch: PropTypes.func,
  filters: PropTypes.array,
  toggleFilter: PropTypes.func,
  sortOrder: PropTypes.func,
  setSortOrder: PropTypes.func,
  determineSortIcon: PropTypes.func,
};

export default function OrganizationUsersDeprecated({ organizationUsers }) {
  return (
    <InstitutionSettingsContextProvider>
      <div style={{ minHeight: "800px" }}>
        <OrganizationUserList
          organizationUsers={organizationUsers}
          renderContainer={Container}
          determineSortIcon={determineSortIcon}
        />
      </div>
    </InstitutionSettingsContextProvider>
  );
}

OrganizationUsersDeprecated.propTypes = {
  organizationUsers: PropTypes.array.isRequired,
};
